/**
 * `SCR-ORDDET` (12 states), `SCR-CONFIRM` (11), `SCR-TRANSITION` (6),
 * `SCR-HALT`/`SCR-RESUME` (7), `SCR-CUTSHEET` (7). FLOWS_SPEC §10.3–§10.5.
 * OWNER: web-frontend-engineer.
 *
 * ONE PAGE, three regions in this vertical order: header · THE CUT SHEET IN
 * FULL, INLINE · action and history. The cut sheet is the page.
 *
 * The transition control renders EXACTLY the legal events for this state and
 * this role, TAKEN FROM THE SERVER (`allowedEvents`). The front end holds no
 * copy of the transition table — which is why `ST-ORDDET-ILLEGAL` can render
 * the legal set from the refusal's own body.
 */

import { useEffect, useRef, useState } from 'react';
import { t, tDynamic } from '@pt/copy';
import type { OrderState, OrderTransitionEvent } from '@pt/core';
import { api, idempotencyKey } from '../../lib/api.ts';
import { refusalCopy } from '../../lib/refusal.ts';
import { useResource, useSubmission } from '../../lib/resource.ts';
import type { AtelierOrderPayload } from '../../lib/contracts.ts';
import {
  ageInWords,
  dateInWords,
  dateInputValue,
  joinList,
  metres,
  money,
  monthsSince,
  shortHash,
  timeOfDay,
} from '@pt/view';
import { AppShell } from '../../app/AppShell.tsx';
import { useAnnouncer } from '../../app/Announcer.tsx';
import { useNavigation } from '../../app/Router.tsx';
import { useSession } from '../../app/SessionProvider.tsx';
import {
  ConfirmStep,
  PromisedWindowField,
  TransitionControl,
  TransitionRefusal,
} from '../../components/backoffice.tsx';
import { CutSheet, CutSheetFailed } from '../../components/cutsheet.tsx';
import { PaymentPanel } from '../../components/payments.tsx';
import { BilingualWorkOrder } from '../../components/bilingual.tsx';
import {
  AssignCutterForm,
  OrderNoteForm,
  RepromiseForm,
} from '../../components/orderActions.tsx';
import { Button, ErrorStatement, FactLine, RovingRadioGroup, Statement, TextInput } from '../../components/primitives.tsx';
import { FetchError, LoadingRegion, OfflineStatement, Refused } from '../../components/states.tsx';

const TERMINAL: readonly OrderState[] = ['delivered', 'cancelled'];
const HALT_CODES = [
  'cloth_flaw',
  'insufficient_cloth',
  'instruction_unclear',
  'measurement_query',
  'client_query',
  'awaiting_client',
  'other',
] as const;

export function AtelierOrderScreen({ orderRef }: { orderRef: string }) {
  const { navigate } = useNavigation();
  const { session } = useSession();
  const { say, refuse } = useAnnouncer();
  const order = useResource(() => api.atelierOrder(orderRef), [orderRef]);
  const submission = useSubmission();

  const [chosenEvent, setChosenEvent] = useState<OrderTransitionEvent | null>(null);
  const [note, setNote] = useState('');
  const [haltCode, setHaltCode] = useState<string>('cloth_flaw');
  const [haltReason, setHaltReason] = useState('');
  const [clientMessage, setClientMessage] = useState('');
  const [resolution, setResolution] = useState('');
  const [window_, setWindow] = useState({ earliest: '', latest: '' });
  const [fieldError, setFieldError] = useState<string | null>(null);
  const refusalRef = useRef<HTMLDivElement>(null);

  const data = order.data;
  const rejection = submission.rejection;

  useEffect(() => {
    if (rejection === null) return;
    refuse(refusalCopy(rejection));
    refusalRef.current?.focus();
    // The control set reloads from the server; the typed note is preserved.
    order.reload();
  }, [rejection]);

  if (data === null) {
    /* A REFUSAL IS NOT A FAILURE, and this screen used to say it was.
       `orderIsOnTheBenchFor` scopes an order to the people working on it: a
       fitter reaches an order in fitting, the workshop reaches one being made,
       and anything else answers 404 — because "does this order exist" is itself
       private. `FetchError` then told them "we couldn't load this, this is our
       side, try again", which is a false apology for a door that is locked on
       purpose, and it invites a person to keep pulling the handle. Nine of the
       eleven seeded orders read like a broken app to the fitter.

       So a refusal renders the refusal: ST-404's own words, the same page
       ST-403 renders, with no retry — there is nothing to retry. */
    const refused = order.rejection?.body.code === 'NOT_FOUND_OR_NOT_YOURS';
    return (
      <AppShell title={orderRef} banner={order.phase === 'offline' ? <OfflineStatement /> : null}>
        {order.phase === 'skeleton' || order.phase === 'idle' ? (
          <LoadingRegion rows={10} variant="ledger" slow={order.slow} label={orderRef} />
        ) : null}
        {order.phase !== 'error' ? null : refused ? (
          <Refused kind={404} />
        ) : (
          <FetchError onRetry={order.reload} />
        )}
      </AppShell>
    );
  }

  const terminal = TERMINAL.includes(data.state);
  const isMasterTailor = session?.role === 'master_tailor';
  const isBackOffice = session?.role === 'master_tailor' || session?.role === 'admin';
  const confirmationGate = data.state === 'placed';

  return (
    <AppShell
      title={t('orddet.heading', { orderRef: data.orderRef })}
      banner={order.phase === 'offline' ? <OfflineStatement /> : null}
    >
      {/* 1. Header. */}
      <dl>
        <FactPairRow label={t('orddet.header.client')} value={data.clientName} />
        <FactPairRow label={t('orddet.header.stage')} value={tDynamic(`atelier.stage.${data.state}`)} />
        <FactPairRow label={t('orddet.header.placed')} value={dateInWords(data.placedAt)} />
        <FactPairRow
          label={t('orddet.header.promised')}
          value={
            data.promisedWindow === null
              ? t('cutsheet.nodate')
              : `${dateInWords(data.promisedWindow.earliest)} – ${dateInWords(data.promisedWindow.latest)}`
          }
        />
        {/* THE WORKSHOP'S HEADER CARRIES NO FIGURE, and says so in the
            server's own words. `middleware/price-blind.ts` removes
            `priceMinor` and `currency` for a `production` principal rather
            than blanking them; this row called `money(undefined, undefined)`,
            which throws, so EVERY order was a blank error page for the people
            who make the suits. A dash would be no better: a row labelled
            "Value" with nothing in it is an invitation to go and find out. */}
        <FactPairRow
          label={t('orddet.header.value')}
          value={
            data.priceMinor === undefined || data.currency === undefined
              ? (data.commercialsWithheld ?? t('orddet.commercialsWithheld'))
              : money(data.priceMinor, data.currency)
          }
        />
        <FactPairRow
          label={t('orddet.header.tailor')}
          value={data.assignedTailorName ?? t('orddet.header.cutter.unassigned')}
        />
        <FactPairRow
          label={t('orddet.header.cutter')}
          value={data.assignedCutterName ?? t('orddet.header.cutter.unassigned')}
        />
      </dl>

      {/* ST-ORDDET-SUPERSEDEDRECORD — stated prominently, blocks nothing. */}
      {data.boundRecord?.supersededBy == null ? null : (
        <Statement>
          {t('orddet.supersededRecord.statement', {
            recordShort: shortHash(data.boundRecord.recordId),
            successorShort: shortHash(data.boundRecord.supersededBy),
          })}
        </Statement>
      )}

      {/* ST-CONFIRM-* — the confirmation gate replaces nothing; it is the same
          page while `state = placed`. */}
      {confirmationGate ? (
        <ConfirmationGatePanels data={data} isMasterTailor={isMasterTailor} />
      ) : null}

      {/* 2. THE CUT SHEET, IN FULL, INLINE. Never a partial sheet. */}
      {data.cutSheetFailed === true || data.cutSheet === null ? (
        <CutSheetFailed onRetry={order.reload} messageKey="orddet.cutsheetFailed.statement" />
      ) : (
        <>
          <CutSheet
            sheet={data.cutSheet}
            amended={data.cutSheet.amended === true}
            onPrint={() => {
              void api.recordCutSheetPrint(data.orderId, { format: 'a4' }).finally(() => window.print());
            }}
          />
          {/* LAW 59: the same sheet as one ticket a garment, for the factory. */}
          <div className="pt-actions pt-noprint">
            <Button onClick={() => navigate(`/atelier/orders/${orderRef}/tickets`)}>{t('tickets.heading')}</Button>
          </div>
        </>
      )}

      {/* 3. Action and history. */}
      <section className="pt-section" aria-label={t('orddet.history.heading')}>
        <div ref={refusalRef} tabIndex={-1}>
          {rejection === null ? null : <TransitionRefusal rejection={rejection} data={data} />}
        </div>

        <TransitionControl
          allowedEvents={data.allowedEvents}
          terminal={terminal}
          onChoose={(event) => {
            setChosenEvent(event);
            setFieldError(null);
            submission.clear();
          }}
          notMineStatement={
            confirmationGate && !isMasterTailor
              ? t('confirm.notTailor.statement', { tailorName: data.assignedTailorName ?? '' })
              : data.allowedEvents.length === 0
                ? t('orddet.notmine.awaitingTailor', { tailorName: data.assignedTailorName ?? '' })
                : null
          }
        />

        {chosenEvent === null ? null : (
          <ConfirmStep
            heading={tDynamic(`event.${chosenEvent}.label`)}
            consequence={
              chosenEvent === 'order.halt'
                ? t('halt.statement', { stageLabel: tDynamic(`atelier.stage.${data.state}`) })
                : chosenEvent === 'order.resume'
                  ? t('resume.statement', { stageLabel: tDynamic(`atelier.stage.${data.state}`) })
                  : tDynamic(`event.${chosenEvent}.consequence`)
            }
            irreversible={
              chosenEvent === 'order.cut'
                ? t('trans.cut.irreversible')
                : ['order.confirm', 'order.cancel', 'order.fitting_passed', 'order.deliver'].includes(
                      chosenEvent,
                    )
                  ? t('trans.irreversible')
                  : null
            }
            proceedLabel={tDynamic(`event.${chosenEvent}.label`)}
            busyLabel={t('trans.busy')}
            busy={submission.busy}
            onStepBack={() => setChosenEvent(null)}
            refusal={
              fieldError === null ? null : <ErrorStatement what={fieldError} />
            }
            onProceed={() => {
              if (chosenEvent === 'order.halt' && haltReason.trim() === '') {
                setFieldError(t('error.HALT_REASON_REQUIRED'));
                return;
              }
              if (chosenEvent === 'order.resume' && resolution.trim() === '') {
                setFieldError(t('error.RESUME_RESOLUTION_REQUIRED'));
                return;
              }
              const payload: Record<string, unknown> = {};
              if (chosenEvent === 'order.halt') {
                payload['haltCode'] = haltCode;
                payload['reason'] = haltReason;
                if (clientMessage.trim() !== '') payload['clientMessage'] = clientMessage;
              }
              if (chosenEvent === 'order.resume') payload['resolution'] = resolution;
              if (chosenEvent === 'order.confirm' && window_.earliest !== '' && window_.latest !== '') {
                payload['promisedWindow'] = { ...window_ };
              }
              void submission
                .run(() =>
                  api.transition(data.orderId, {
                    event: chosenEvent,
                    expectedFrom: data.state,
                    note,
                    payload,
                    idempotencyKey: idempotencyKey(),
                  }),
                )
                .then((result) => {
                  if (result === null) return;
                  setChosenEvent(null);
                  setNote('');
                  say(t('trans.done.announce', { stageLabel: tDynamic(`atelier.stage.${result.state}`) }));
                  order.reload();
                });
            }}
          >
            {chosenEvent === 'order.halt' ? (
              <>
                <fieldset className="pt-filter">
                  <legend className="pt-filter__legend">{t('halt.code.label')}</legend>
                  <RovingRadioGroup
                    ariaLabel={t('halt.code.label')}
                    values={HALT_CODES}
                    chosen={haltCode}
                    labelOf={(code) => tDynamic(`halt.code.${code}`)}
                    onChoose={setHaltCode}
                  />
                </fieldset>
                <TextInput
                  label={t('halt.reason.label')}
                  hint={t('halt.reason.help')}
                  value={haltReason}
                  onChange={setHaltReason}
                  multiline
                />
                <TextInput
                  label={t('halt.clientMessage.label')}
                  hint={t('halt.clientMessage.help')}
                  value={clientMessage}
                  onChange={setClientMessage}
                  multiline
                />
              </>
            ) : null}

            {chosenEvent === 'order.resume' ? (
              <>
                {data.clothPanel === null ? null : (
                  <FactLine>
                    {t('resume.stock.statement', {
                      clothName: data.clothPanel.clothName,
                      availableMetres: metres(data.clothPanel.availableMetres),
                      requirementMetres: metres(data.clothPanel.requirementMetres),
                    })}
                  </FactLine>
                )}
                <TextInput
                  label={t('resume.resolution.label')}
                  value={resolution}
                  onChange={setResolution}
                  multiline
                />
              </>
            ) : null}

            {/* ST-CONFIRM-WINDOW — EMPTY ON LOAD, with the house lead time as a
                labelled one-action fill. Never a pre-fill. */}
            {chosenEvent === 'order.confirm' ? (
              <PromisedWindowField
                earliest={window_.earliest}
                latest={window_.latest}
                onChange={setWindow}
                houseWindow={
                  data.houseLeadTimeWindow === null
                    ? null
                    : {
                        earliest: dateInputValue(data.houseLeadTimeWindow.earliest),
                        latest: dateInputValue(data.houseLeadTimeWindow.latest),
                      }
                }
              />
            ) : null}

            <TextInput label={t('trans.note.label')} value={note} onChange={setNote} multiline />
          </ConfirmStep>
        )}

        {/* ROAD-5 · THE THREE RECORDED ACTS THAT ARE NOT TRANSITIONS. They sit
            BELOW the transition control and never inside it: OPS_SPEC §2.4
            keeps two lists precisely so a person can tell "record the cloth
            cut" from "who is making it", and a menu that mixes them is how
            somebody cuts cloth while meaning to leave a note.

            A note is any staff role's — the workshop leaving one is the whole
            point of an appendix. Assigning a cutter and moving a promised date
            are the back office's: a name on an order is who a question goes to,
            and a date already given to a man is not moved by whoever is
            nearest. */}
        <OrderNoteForm data={data} onWrote={order.reload} />
        {isBackOffice ? (
          <>
            <AssignCutterForm data={data} onWrote={order.reload} />
            <RepromiseForm data={data} onWrote={order.reload} />
          </>
        ) : null}

        {/* ROAD-4 · THE WORDS, for the people who read the sheet. The block has
            been on this payload since P-THAI-01 and nothing rendered it, which
            meant law 31's "in both english and in thai" was true of the server
            and of nothing a person could see. It sits with the sheet's other
            appendices, and it prints: a cutter reading this on paper in Bangkok
            is exactly who it is for. */}
        {data.bilingual === undefined ? null : (
          <BilingualWorkOrder
            block={data.bilingual}
            {...(isBackOffice ? { onOpenGlossary: () => navigate('/atelier/glossary') } : {})}
          />
        )}

        {/* ROAD-2 · THE MONEY, BESIDE THE TRANSITIONS AND NEVER AMONG THEM.
            `domain/payments.ts`: money changes no order state. A deposit drawn
            inside the transition control would teach a tailor the opposite, and
            the workshop would look like it starts cutting on a webhook.

            BACKOFFICE ONLY, and this is the front stop rather than the backstop:
            every payment route is BACKOFFICE, and `middleware/price-blind.ts`
            would mask the figures for `production` anyway — but a panel a
            price-blind role can see the shape of is a panel somebody has to
            remember to strip. It is simply not rendered. */}
        {isBackOffice ? <PaymentPanel orderId={data.orderId} /> : null}

        <h2 className="pt-record__groupHeading pt-section">{t('orddet.history.heading')}</h2>
        <ol>
          {data.appendix.map((entry) => (
            <li key={`${entry.event}-${entry.at}`} className="pt-timeline__entry">
              <span className="pt-timeline__date">{dateInWords(entry.at)}</span>
              <span className="pt-timeline__stage">{tDynamic(`event.${entry.event}.label`)}</span>
              <span>{entry.actorName}</span>
            </li>
          ))}
        </ol>

        <div className="pt-actions">
          <Button onClick={() => navigate(`/atelier/clients/${data.clientId}`)}>
            {t('global.nav.clients')}
          </Button>
        </div>
      </section>
    </AppShell>
  );
}

/**
 * The bound-record line, crash-proofed the same way `tDynamic` degrades: a
 * projection that omits `authoredByName`/`reason` must NEVER print the literal
 * "undefined" (clarity-verify NEW-1). A missing author falls back to the house
 * itself (the record IS the atelier's — an honest neutral, not an invented
 * person); a missing reason drops its clause rather than dangle an empty
 * separator. No new string is authored.
 */
function boundRecordLine(record: NonNullable<AtelierOrderPayload['boundRecord']>): string {
  const authorName = (record.authoredByName ?? '').trim() || t('global.houseName');
  const reason = (record.reason ?? '').trim();
  const line = t('confirm.record.line', {
    recordShort: shortHash(record.recordId),
    contentHashShort: shortHash(record.contentHash),
    takenAtDate: dateInWords(record.takenAt),
    authorName,
    reason,
  });
  return reason === '' ? line.replace(/\s*·\s*$/, '') : line;
}

function FactPairRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="pt-fact pt-fact--pair">
      <dt className="pt-fact__label">{label}</dt>
      <dd className="pt-fact__value">{value}</dd>
    </div>
  );
}

/**
 * `ConfirmationGate` — the five panels of `SCR-CONFIRM`, all on the page, with
 * NO EXPANDING, before the actions, so nothing has to be counted or hunted.
 */
function ConfirmationGatePanels({
  data,
  isMasterTailor,
}: {
  data: AtelierOrderPayload;
  isMasterTailor: boolean;
}) {
  const record = data.boundRecord;
  const completeness = data.completeness ?? [];
  const advisories = data.advisories ?? [];
  const overrides = data.overrides ?? [];
  return (
    <div>
      {/* 1. Completeness — the per-garment assertion. Rendered when the server
          has computed it; a payload that omits it shows no empty heading (an
          absent panel is not a crash — clarity Finding 2). */}
      {completeness.length === 0 ? null : (
        <section className="pt-block">
          <h2 className="pt-cutsheet__blockTitle">{t('confirm.completeness.heading')}</h2>
          {completeness.map((entry) => (
            <FactLine key={entry.garment}>
              {t('confirm.completeness.entry', {
                garmentLabel: tDynamic(`global.garment.${entry.garment}`),
                recordedCount: entry.recorded,
                applicableCount: entry.applicable,
              })}
            </FactLine>
          ))}
        </section>
      )}

      {/* 2. The bound record. */}
      {record === null ? null : (
        <section className="pt-block">
          <h2 className="pt-cutsheet__blockTitle">{t('confirm.record.heading')}</h2>
          <FactLine>{boundRecordLine(record)}</FactLine>
          {monthsSince(record.takenAt) > 18 ? (
            <FactLine>
              {t('confirm.aged.notAccepted', { ageInWords: ageInWords(record.takenAt) })}
            </FactLine>
          ) : null}
          {record.provenance === 'legacy_import' && record.verifiedBy === null ? (
            <>
              <FactLine>{t('confirm.legacy.statement')}</FactLine>
              <FactLine>{t('confirm.legacy.verify.help')}</FactLine>
              {isMasterTailor ? (
                <div className="pt-actions">
                  <Button onClick={() => void api.verifyMeasurementRecord(record.recordId)}>
                    {t('confirm.legacy.verify')}
                  </Button>
                </div>
              ) : null}
            </>
          ) : null}
          {record.supersededBy === null ? null : (
            <Statement>
              {t('confirm.superseded.statement', { successorShort: shortHash(record.supersededBy) })}
            </Statement>
          )}
          {/* ST-CONFIRM-HASHFAIL — records are immutable, so a mismatch can only
              mean corruption. Hard refuse, no retry offered. */}
          {data.hashOk ? null : <ErrorStatement what={t('confirm.hashfail.statement')} assertive />}
        </section>
      )}

      {/* 3. The cloth, as it stands NOW. */}
      {data.clothPanel === null ? null : (
        <section className="pt-block">
          <h2 className="pt-cutsheet__blockTitle">{t('confirm.cloth.heading')}</h2>
          <FactLine>
            {t('confirm.cloth.line', {
              clothName: data.clothPanel.clothName,
              requirementMetres: metres(data.clothPanel.requirementMetres),
              reservedMetres: metres(data.clothPanel.reservedMetres),
              boltId: data.clothPanel.boltId,
              availableMetres: metres(data.clothPanel.availableMetres),
            })}
          </FactLine>
          {data.stockOk ? null : (
            <ErrorStatement
              what={t('confirm.stockfail.statement', {
                availableMetres: metres(data.clothPanel.availableMetres),
                requirementMetres: metres(data.clothPanel.requirementMetres),
              })}
              assertive
            />
          )}
        </section>
      )}

      {/* 4. Advisories and released rules. */}
      <section className="pt-block">
        <h2 className="pt-cutsheet__blockTitle">{t('confirm.advisories.heading')}</h2>
        {advisories.length === 0 && overrides.length === 0 ? (
          <FactLine>{t('confirm.advisories.empty')}</FactLine>
        ) : (
          <>
            {advisories.map((advisory) => (
              <FactLine key={advisory.constraintId}>{advisory.text}</FactLine>
            ))}
            {overrides.map((override) => (
              <FactLine key={override.constraintId}>
                {`${override.constraintId} · ${override.tailorName} · ${override.reason}`}
              </FactLine>
            ))}
          </>
        )}
      </section>

      {/* 5. Commercials — §13.4: the client was shown NO deposit terms. */}
      <section className="pt-block">
        <h2 className="pt-cutsheet__blockTitle">{t('confirm.commercials.heading')}</h2>
        {data.priceMinor === undefined || data.currency === undefined ? (
          <FactLine>{data.commercialsWithheld ?? t('orddet.commercialsWithheld')}</FactLine>
        ) : (
          <>
            <FactLine>{money(data.priceMinor, data.currency)}</FactLine>
            <FactLine>{t('confirm.commercials.terms')}</FactLine>
          </>
        )}
      </section>

      {/* 6. Any client request — verbatim, so the tailor confirms with it in
          front of him. Hold is the indicated act, stated, never enforced. */}
      {data.openClientRequest === null ? null : (
        <section className="pt-block">
          <h2 className="pt-cutsheet__blockTitle">{t('order.askable.change')}</h2>
          <p className="pt-record__notes">{data.openClientRequest.text}</p>
          <FactLine>{dateInWords(data.openClientRequest.at)}</FactLine>
          <Statement>{t('confirm.hold')}</Statement>
        </section>
      )}
    </div>
  );
}
